import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button } from "react-aria-components";

import { cn } from "@/utils/cn";

import { EditButton } from "./EditButton";

type CollapsibleCardProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  /** Shows an edit button in the header; editing also expands the card. */
  onEdit?: () => void;
  title: string;
};

export function CollapsibleCard({
  children,
  defaultOpen = true,
  onEdit,
  title,
}: CollapsibleCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="rounded-2xl border border-border-secondary bg-bg-primary shadow-xs">
      <header className="flex items-center gap-1 px-3xl py-5">
        <h2 className="flex-1 text-lg font-bold text-text-primary">{title}</h2>
        {onEdit && (
          <EditButton
            label={title}
            onPress={() => {
              setIsOpen(true);
              onEdit();
            }}
          />
        )}
        <Button
          aria-expanded={isOpen}
          aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
          className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-fg-quaternary outline-hidden transition-colors hover:bg-bg-primary-hover hover:text-text-primary"
          onPress={() => setIsOpen((v) => !v)}
        >
          <ChevronDown
            className={cn(
              "size-5 transition-transform duration-300",
              isOpen && "rotate-180",
            )}
          />
        </Button>
      </header>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-3xl pb-3xl">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
